import { ReactNode, useEffect } from 'react';

import { PostCheckoutDecisionModal } from './PostCheckoutDecisionModal';
import { useQueryParam } from '../../../../hooks/useQueryParam';
import { useAwaitPostCheckout } from '../../hooks/useAwaitPostCheckout';
import { AccountContentSkeleton } from '../AccountContentSkeleton/AccountContentSkeleton';

const POST_CHECKOUT_PARAMS = ['postCheckout', 'awaitDecision'];

/**
 * After checkout (including repeat-customer redirects), the backend may
 * still be processing — funding the paylink or re-decisioning. This guard
 * polls until the backend state has settled before rendering its children,
 * so the dashboard never flashes stale data.
 *
 * When `awaitDecision=true` is in the URL (repeat-customer redirects),
 * the guard also accepts a valid formal offer as a settled state.
 * Otherwise it only waits for funding or capture (normal drawdown flow).
 *
 * When there is no post-checkout context, children render immediately.
 */
export function PostCheckoutGuard({
  isMobile,
  children,
}: {
  isMobile: boolean;
  children: ReactNode;
}) {
  const payLinkId = useQueryParam('payLinkId');
  const postCheckout = useQueryParam('postCheckout');
  const awaitDecision = useQueryParam('awaitDecision') === 'true';

  if (payLinkId && postCheckout === 'true') {
    return (
      <AwaitSettledGate
        payLinkId={payLinkId}
        awaitDecision={awaitDecision}
        isMobile={isMobile}
      >
        {children}
      </AwaitSettledGate>
    );
  }

  return children;
}

function AwaitSettledGate({
  payLinkId,
  awaitDecision,
  isMobile,
  children,
}: {
  payLinkId: string;
  awaitDecision: boolean;
  isMobile: boolean;
  children: ReactNode;
}) {
  const { isReady } = useAwaitPostCheckout(payLinkId, { awaitDecision });

  useEffect(() => {
    if (!isReady) return;

    const url = new URL(window.location.href);
    POST_CHECKOUT_PARAMS.forEach((p) => url.searchParams.delete(p));
    window.history.replaceState(null, '', url.pathname + url.search);
  }, [isReady]);

  if (!isReady) {
    return (
      <>
        {awaitDecision && <PostCheckoutDecisionModal isOpen />}
        <AccountContentSkeleton isMobile={isMobile} />
      </>
    );
  }

  return children;
}
